/**
 * Reports — recruitment analytics. Tabs: the pipeline Funnel (current
 * occupancy), cohort Conversion (reached-stage + stage-to-stage rates),
 * Screening (disqualification analytics at the three gates — Intake /
 * Questionnaire / Prescreen), Calls (per-recruiter calling), Productivity
 * (daily recruiter activity), and Metrics (headline sales KPIs). Each is
 * sliceable overall / by study / by source / by recruiter (where it applies)
 * with a time range, and a GLOBAL study selector scopes every tab. Selections
 * live in the URL (shareable) and persist via getPref.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import api from '../../services/api';
import { StudyOption, studyInternalLabel } from '../types';
import { getPref, setPref, rangeWindow, useIsCRMAdmin } from '../lib';
import FunnelReport from '../components/reports/FunnelReport';
import StageConversionReport from '../components/reports/StageConversionReport';
import DQReport, { DQGroupBy } from '../components/reports/DQReport';
import ScreeningGateReport from '../components/reports/ScreeningGateReport';
import CallsReport from '../components/reports/CallsReport';
import ProductivityReport from '../components/reports/ProductivityReport';
import DispositionsReport from '../components/reports/DispositionsReport';
import MetricsReport from '../components/reports/MetricsReport';
import CommsFeedReport from '../components/reports/CommsFeedReport';
import { FilterBar, FilterSelect, PageHeader, SegmentedControl, SegmentedTabs } from '../components';

const TABS = [
  { key: 'funnel', label: 'Funnel' },
  { key: 'conversion', label: 'Conversion' },
  { key: 'screening', label: 'Screening' },
  { key: 'dispositions', label: 'Dispositions' },
  { key: 'calls', label: 'Calls' },
  { key: 'productivity', label: 'Productivity' },
  { key: 'metrics', label: 'Metrics' },
] as const;

// The three disqualification gates inside the Screening tab. Intake +
// Questionnaire are the new ScreeningGateReport; Prescreen is the DQ ledger.
const SCREENING_GATES = [
  { value: 'intake', label: 'Intake' },
  { value: 'questionnaire', label: 'Questionnaire' },
  { value: 'prescreen', label: 'Prescreen' },
] as const;

// Funnel + Conversion share the same group-by vocabulary.
const FUNNEL_GROUPS = [
  { value: 'overall', label: 'Overall' },
  { value: 'study', label: 'By study' },
  { value: 'lead_source', label: 'By lead source' },
  { value: 'recruiter', label: 'By recruiter' },
] as const;

const DQ_GROUPS = [
  { value: 'category', label: 'By reason category' },
  { value: 'criterion', label: 'By criterion' },
  { value: 'study', label: 'By study' },
  { value: 'lead_source', label: 'By lead source' },
  { value: 'recruiter', label: 'By recruiter' },
] as const;

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
] as const;

const FUNNEL_GROUP_TABS = new Set(['funnel', 'conversion']);

export default function Reports() {
  const [params, setParams] = useSearchParams();
  const [studies, setStudies] = useState<StudyOption[]>([]);

  // The live communications board is admin-only — it surfaces message bodies +
  // patient names (PHI). Non-admins never see the tab, and the backend gates the
  // endpoint with requireCrmAdmin, so a hand-typed ?tab=live falls back to Funnel.
  const isAdmin = useIsCRMAdmin();
  const TAB_LIST = isAdmin ? [...TABS, { key: 'live', label: 'Live feed' }] : [...TABS];

  const tab = TAB_LIST.some((t) => t.key === params.get('tab')) ? params.get('tab')! : 'funnel';
  const usesFunnelGroups = FUNNEL_GROUP_TABS.has(tab);
  const range = RANGES.some((r) => r.value === params.get('range'))
    ? params.get('range')!
    : getPref('reports.range', '30');

  // Screening gate (Intake / Questionnaire / Prescreen) — persisted across tabs.
  const gateParam = params.get('gate') || getPref('reports.screening.gate', 'intake');
  const gate = SCREENING_GATES.some((g) => g.value === gateParam) ? gateParam : 'intake';
  const isPrescreen = tab === 'screening' && gate === 'prescreen';

  // Group-by applies to funnel/conversion (funnel groups) and the prescreen gate
  // (DQ groups). Its pref key + default depend on which of those is active.
  const groupPrefKey = usesFunnelGroups ? `reports.${tab}.group` : 'reports.screening.prescreen.group';
  const groupDefault = usesFunnelGroups ? 'overall' : 'category';
  const groupParam = params.get('group') || getPref(groupPrefKey, groupDefault);
  const funnelGroup = FUNNEL_GROUPS.some((g) => g.value === groupParam) ? groupParam : 'overall';
  const dqGroup = DQ_GROUPS.some((g) => g.value === groupParam) ? (groupParam as DQGroupBy) : 'category';

  // Global study filter (scopes every tab). Persisted across tabs.
  const studyId = params.get('study') || getPref('reports.study', '') || '';

  // The assignable list of real (crm.studies) studies for the global selector.
  useEffect(() => {
    let alive = true;
    api
      .get('/api/crm/studies')
      .then((res) => {
        if (!alive) return;
        const list: StudyOption[] = (res.data?.studies || res.data || []).filter((s: StudyOption) => s.id);
        setStudies(list);
      })
      .catch(() => { /* selector just stays "All studies" */ });
    return () => { alive = false; };
  }, []);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // The group default depends on tab+gate, so reset it when either changes.
    if (key === 'tab' || key === 'gate') next.delete('group');
    setParams(next, { replace: true });
    if (key === 'range') setPref('reports.range', value);
    if (key === 'group') setPref(groupPrefKey, value);
    if (key === 'study') setPref('reports.study', value);
    if (key === 'gate') setPref('reports.screening.gate', value);
  };

  // Persisted defaults still land in the URL so copied links reproduce the view.
  useEffect(() => {
    if (!params.get('range') || !params.get('tab') || (!params.get('study') && studyId)) {
      const next = new URLSearchParams(params);
      if (!next.get('tab')) next.set('tab', tab);
      if (!next.get('range')) next.set('range', range);
      if (!next.get('study') && studyId) next.set('study', studyId);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconcile a persisted/shared study filter that doesn't exist in this
  // network's study list (a deleted study, or a cross-network ?study=<id> link):
  // clear it so the picker ("All studies") and the actual report scope agree.
  // Only after a SUCCESSFUL, non-empty fetch — a transient fetch failure leaves
  // studies=[] and must not silently drop a valid filter.
  useEffect(() => {
    if (studyId && studies.length > 0 && !studies.some((s) => s.id === studyId)) {
      setParam('study', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studies, studyId]);

  const { from, to } = useMemo(() => rangeWindow(range), [range]);

  const sid = studyId || null;
  const showGroup = usesFunnelGroups || isPrescreen;
  const groups = usesFunnelGroups ? FUNNEL_GROUPS : DQ_GROUPS;
  const group = usesFunnelGroups ? funnelGroup : dqGroup;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={BarChart3}
        title="Reports"
        subtitle="Pipeline conversion, recruiter productivity, and the reasons leads don't qualify."
      />

      {/* Report-type tabs */}
      <SegmentedTabs
        label="Report type"
        tabs={TAB_LIST.map((t) => ({ value: t.key, label: t.label }))}
        value={tab}
        onChange={(v) => setParam('tab', v)}
      />

      {/* Toolbar: screening gate toggle + global study / group / range selects */}
      <FilterBar>
        {tab === 'screening' && (
          <SegmentedControl
            label="Screening gate"
            value={gate}
            onChange={(v) => setParam('gate', v)}
            options={SCREENING_GATES.map((g) => ({ value: g.value, label: g.label }))}
          />
        )}
        <FilterSelect
          value={studyId}
          onChange={(v) => setParam('study', v)}
          auto
          label="Study"
          title="Scope every tab to one study (by each lead's current study)"
        >
          <option value="">All studies</option>
          {studies.map((s) => (
            <option key={s.id!} value={s.id!}>{studyInternalLabel(s)}</option>
          ))}
        </FilterSelect>
        {showGroup && (
          <FilterSelect value={group} onChange={(v) => setParam('group', v)} auto label="Group by">
            {groups.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </FilterSelect>
        )}
        <FilterSelect value={range} onChange={(v) => setParam('range', v)} auto label="Time range">
          {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </FilterSelect>
      </FilterBar>

      {tab === 'funnel' ? (
        <FunnelReport groupBy={funnelGroup as any} from={from} to={to} studyId={sid} />
      ) : tab === 'conversion' ? (
        <StageConversionReport groupBy={funnelGroup as any} from={from} to={to} studyId={sid} />
      ) : tab === 'calls' ? (
        <CallsReport from={from} to={to} studyId={sid} />
      ) : tab === 'dispositions' ? (
        <DispositionsReport from={from} to={to} studyId={sid} />
      ) : tab === 'productivity' ? (
        <ProductivityReport from={from} to={to} studyId={sid} />
      ) : tab === 'metrics' ? (
        <MetricsReport from={from} to={to} studyId={sid} />
      ) : tab === 'live' ? (
        <CommsFeedReport from={from} to={to} studyId={sid} />
      ) : tab === 'screening' ? (
        gate === 'prescreen' ? (
          <DQReport groupBy={dqGroup} from={from} to={to} studyId={sid} />
        ) : (
          <ScreeningGateReport gate={gate as 'intake' | 'questionnaire'} from={from} to={to} studyId={sid} />
        )
      ) : null}
    </div>
  );
}
